import type { Event as SentryEvent } from '@sentry/vue';

/**
 * Detects Chameleon's own `t.getThemeId is not a function` crash.
 *
 * Chameleon's widget script, loaded at runtime from its CDN as
 * `/assets/chmln/...` chunks, throws this through `window.onerror` on page
 * loads. It is not ours to fix, and at tens of
 * thousands of events a day it exhausts the org's Sentry error quota, which
 * silences every other error in the project.
 *
 * Matches only when the message is the known one *and* every exception in the
 * event (including linked causes) has a non-empty stack made entirely of
 * Chameleon frames. An app error that wraps a Chameleon failure, or one with
 * no frames to judge by, is still reported.
 */
const CHAMELEON_FRAME_PATTERN = /\/chmln\//;
const THEME_ID_MESSAGE_PATTERN = /\bgetThemeId is not a function\b/;

export function isChameleonThemeIdError(event: SentryEvent): boolean {
  const exceptions = event.exception?.values;
  if (!exceptions || exceptions.length === 0) {
    return false;
  }

  if (!exceptions.some((exception) => THEME_ID_MESSAGE_PATTERN.test(exception.value ?? ''))) {
    return false;
  }

  return exceptions.every((exception) => {
    const frames = exception.stacktrace?.frames;
    if (!frames || frames.length === 0) {
      return false;
    }
    return frames.every((frame) => CHAMELEON_FRAME_PATTERN.test(frame.filename ?? ''));
  });
}
